/** @jsxImportSource @emotion/react */

import { jsonToCsv, formatCurrency } from '@quickbit/backoffice-core-frontend';
import {
  MoneyScaled,
  removeScaleFromMoney,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useAsyncFn } from 'react-use';
import {
  CorporateWalletTransactionsApiFilters,
  SortOptions,
  getCorporateWalletTransactions,
  CorporateWalletTransactionsResponse,
  CorporateWalletTransaction,
} from '../api';

const PAGE_SIZE = 50;

interface Props {
  filters: CorporateWalletTransactionsApiFilters;
  sortOptions?: SortOptions;
  fetchTransactions?: () => Promise<CorporateWalletTransactionsResponse | null>;
  merchantId: string;
  disabled?: boolean;
}
type Columns =
  | 'Created At'
  | 'Transaction Id'
  | 'Blockchain Hash'
  | 'Type'
  | 'Amount'
  | 'Amount (EUR)'
  | 'Balance Before'
  | 'Balance Before (EUR)'
  | 'Balance After'
  | 'Balance After (EUR)'
  | 'Initiated By';

type Row = Record<Columns, string | number | MoneyScaled | null | undefined>;

const corporateWalletTransactionToRow = (
  transaction: CorporateWalletTransaction
): Row => {
  return {
    'Created At': transaction.createdAt.toDateString(),
    'Transaction Id': transaction.id,
    'Blockchain Hash': transaction.blockchainTransactionId ?? '-',
    Type: transaction.type,
    Amount: formatCurrency(transaction.amount),
    'Amount (EUR)': removeScaleFromMoney(transaction.amountReference),
    'Balance Before': transaction.balanceAtTransactionTime
      ? formatCurrency(transaction.balanceAtTransactionTime)
      : '-',
    'Balance Before (EUR)': transaction.balanceReferenceAtTransactionTime
      ? removeScaleFromMoney(transaction.balanceReferenceAtTransactionTime)
      : '-',
    'Balance After': transaction.balanceAfterTransaction
      ? formatCurrency(transaction.balanceAfterTransaction)
      : '-',
    'Balance After (EUR)': transaction.balanceReferenceAfterTransaction
      ? removeScaleFromMoney(transaction.balanceReferenceAfterTransaction)
      : '-',
    'Initiated By': transaction.authorizedBy?.email,
  };
};

export const ExportCsv = ({ filters, merchantId, disabled }: Props) => {
  const [{ loading: downloadCsvLoading }, downloadCsv] =
    useAsyncFn(async () => {
      const page = 1;
      const corporateWalletTransactions = await getCorporateWalletTransactions({
        merchantId,
        pagination: { size: PAGE_SIZE, page },
        filters,
      });
      const lastPage = Math.ceil(
        corporateWalletTransactions.aggregatedData.count / PAGE_SIZE
      );
      const data: Row[] = corporateWalletTransactions.transactions.map(
        (transaction) => corporateWalletTransactionToRow(transaction)
      );

      let i = page + 1;
      let errorCount = 0;
      while (i <= lastPage) {
        try {
          const corporateWalletTransaction =
            await getCorporateWalletTransactions({
              merchantId,
              pagination: { size: PAGE_SIZE, page: i },
              filters,
            });
          data.push(
            ...corporateWalletTransaction.transactions.map((transaction) =>
              corporateWalletTransactionToRow(transaction)
            )
          );
          i++;
          errorCount = 0;
        } catch (e) {
          errorCount++;
          if (errorCount >= 5) {
            throw new Error('Failed to fetch order data');
          }
          console.error('Failed to fetch order data, trying again', e);
        }
      }

      jsonToCsv({
        data: data,
        filename: 'CorporateWalletTransactions',
        delimiter: ';',
        columns: [
          'Created At',
          'Transaction Id',
          'Blockchain Hash',
          'Type',
          'Amount',
          'Amount (EUR)',
          'Balance Before',
          'Balance Before (EUR)',
          'Balance After',
          'Balance After (EUR)',
          'Initiated By',
        ] as Columns[],
      });
      return data;
    }, [filters]);

  return (
    <UtilityButton
      text="Extract as csv"
      onClick={downloadCsv}
      loading={downloadCsvLoading}
      disabled={disabled || downloadCsvLoading}
    />
  );
};
